/**
 * DreamRealityRuntime - Universal State Management (T-TL-34)
 * 
 * Manages ALL state types declared in manifest.stateTypes.
 * Works in preview, production, and gate contexts.
 * 
 * Replaces hardcoded condition logic with declarative state system.
 * Enables "design any template like language" capability.
 * 
 * Usage:
 *   const runtime = new DreamRealityRuntime({
 *     stateTypes: manifest.stateTypes,
 *     initialState: { 'hero.mediaType': 'image' }
 *   });
 *   
 *   runtime.setState('hero.mediaType', 'video');
 *   runtime.getState('hero.mediaType'); // 'video'
 */

export interface StateType {
  type: 'enum' | 'boolean' | 'number' | 'string' | 'object';
  values?: any[];
  default: any;
  persist: boolean;
  description?: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
}

export interface RuntimeConfig {
  stateTypes: Record<string, StateType>;
  initialState?: Record<string, any>;
  context?: 'preview' | 'production' | 'gate';
}

export type StateChangeListener = (path: string, value: any) => void;

export class DreamRealityRuntime {
  private stateTypes: Record<string, StateType>;
  private currentState: Record<string, any> = {};
  private context: 'preview' | 'production' | 'gate';
  private listeners: StateChangeListener[] = [];
  private destroyed = false;

  constructor(config: RuntimeConfig) {
    this.stateTypes = config.stateTypes || {};
    this.context = config.context || this.detectContext();
    
    // Initialize state with defaults
    this.initializeState(config.initialState);
    
    // Load persisted state from localStorage
    this.loadPersistedState();
  }

  /**
   * Set state value with validation and persistence
   */
  setState(path: string, value: any, persist?: boolean): void {
    if (this.destroyed) {
      console.warn('[DLR] Runtime destroyed, ignoring setState');
      return;
    }

    // Validate against state type
    if (!this.validateState(path, value)) {
      console.error(`[DLR] Invalid state value for ${path}:`, value);
      return;
    }

    // Update current state
    const oldValue = this.currentState[path];
    this.currentState[path] = value;

    // Persist if needed. An explicit persist argument overrides the state type
    // default so transient UI states can opt out of storage.
    const stateType = this.stateTypes[path];
    const shouldPersist = this.context === 'preview'
      ? false
      : persist ?? Boolean(stateType && stateType.persist);
    if (shouldPersist) {
      this.persistState(path, value);
    }

    // Notify listeners (only if value actually changed)
    if (oldValue !== value) {
      this.notifyListeners(path, value);
    }
  }

  /**
   * Get state value (returns default if not set)
   */
  getState(path: string): any {
    if (path in this.currentState) {
      return this.currentState[path];
    }
    
    const stateType = this.stateTypes[path];
    return stateType ? stateType.default : undefined;
  }

  /**
   * Subscribe to state changes
   * Returns unsubscribe function
   */
  onStateChange(callback: StateChangeListener): () => void {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Get all current state
   */
  getAllState(): Record<string, any> {
    return { ...this.currentState };
  }

  /**
   * Get context (preview/production/gate)
   */
  getContext(): 'preview' | 'production' | 'gate' {
    return this.context;
  }

  /**
   * Clean up listeners and mark as destroyed
   */
  destroy(): void {
    this.listeners = [];
    this.destroyed = true;
  }

  /**
   * Initialize state with defaults and optional initial values
   */
  private initializeState(initialState?: Record<string, any>): void {
    // Set defaults from state types
    for (const [path, stateType] of Object.entries(this.stateTypes)) {
      this.currentState[path] = stateType.default;
    }

    // Override with initial state if provided
    if (initialState) {
      for (const [path, value] of Object.entries(initialState)) {
        if (this.validateState(path, value)) {
          this.currentState[path] = value;
        } else {
          console.warn(`[DLR] Invalid initial state for ${path}:`, value);
        }
      }
    }
  }

  /**
   * Load persisted state from localStorage
   */
  private loadPersistedState(): void {
    if (this.context === 'preview') {
      return;
    }

    for (const [path, stateType] of Object.entries(this.stateTypes)) {
      if (stateType.persist) {
        const key = this.getStorageKey(path);
        try {
          const stored = localStorage.getItem(key);
          if (stored !== null) {
            const value = JSON.parse(stored);
            if (this.validateState(path, value)) {
              this.currentState[path] = value;
            } else {
              console.warn(`[DLR] Invalid persisted state for ${path}, using default`);
            }
          }
        } catch (e) {
          console.warn(`[DLR] Failed to load persisted state for ${path}:`, e);
        }
      }
    }
  }

  /**
   * Persist state to localStorage
   */
  private persistState(path: string, value: any): void {
    const key = this.getStorageKey(path);
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      // Handle quota exceeded or other localStorage errors
      console.error(`[DLR] Failed to persist state for ${path}:`, e);
    }
  }

  /**
   * Get localStorage key for state path
   */
  private getStorageKey(path: string): string {
    return `dlr_state_${path.replace(/\./g, '_')}`;
  }

  /**
   * Validate state value against type definition
   */
  private validateState(path: string, value: any): boolean {
    const stateType = this.stateTypes[path];
    
    // Allow undefined state types for backward compatibility
    if (!stateType) {
      console.warn(`[DLR] No state type defined for ${path}, allowing value`);
      return true;
    }

    // Type-specific validation
    switch (stateType.type) {
      case 'enum':
        if (!stateType.values || !Array.isArray(stateType.values)) {
          console.error(`[DLR] Enum state type ${path} has no values array`);
          return false;
        }
        return stateType.values.includes(value);
        
      case 'boolean':
        return typeof value === 'boolean';
        
      case 'number':
        if (typeof value !== 'number') return false;
        // Check validation rules
        if (stateType.validation) {
          if (stateType.validation.min !== undefined && value < stateType.validation.min) {
            return false;
          }
          if (stateType.validation.max !== undefined && value > stateType.validation.max) {
            return false;
          }
        }
        return true;
        
      case 'string':
        if (typeof value !== 'string') return false;
        // Check pattern validation
        if (stateType.validation?.pattern) {
          try {
            const regex = new RegExp(stateType.validation.pattern);
            return regex.test(value);
          } catch (e) {
            console.error(`[DLR] Invalid regex pattern for ${path}:`, e);
            return true; // Don't fail on bad regex
          }
        }
        return true;
        
      case 'object':
        return typeof value === 'object' && value !== null;
        
      default:
        console.warn(`[DLR] Unknown state type for ${path}:`, stateType.type);
        return true; // Allow unknown types
    }
  }

  /**
   * Detect runtime context from environment
   */
  private detectContext(): 'preview' | 'production' | 'gate' {
    if (typeof window === 'undefined') {
      return 'production';
    }

    // Check for preview mode
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview') === 'true') {
      return 'preview';
    }

    // Check for gate config
    if ((window as any).__DLR) {
      return 'gate';
    }

    return 'production';
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(path: string, value: any): void {
    for (const listener of this.listeners) {
      try {
        listener(path, value);
      } catch (e) {
        console.error('[DLR] Listener error:', e);
      }
    }
  }
}
